import type { PetAbilityLine, PetAbilityNumbers, PetEffectNumbers } from "@highmarch/app";

/**
 * A pet ability in one line, for the pet sheet (Pets v2/v3, docs/PETS.md; docs/VOICE.md: short, the numbers kept): the
 * pup's four kinds and the v3 kinds every other pet's abilities are built from — an attack (`strike`), a passive
 * (`aura`), a pulse and a signature, each with an effect block (damage, burn, slow, stun, mark, dazzle). Strings only,
 * so test/petsV3.test.ts pins them.
 */

const sec = (s: number): string => `${Number.isInteger(s) ? s : s.toFixed(1)} s`;
const cells = (n: number): string => `${Number.isInteger(n) ? n : n.toFixed(1)} cell${n === 1 ? "" : "s"}`;

/** What an effect does to the raiders it touches ("120 fire damage, burning 10 a second for 3 s"). */
export function effectWords(e: PetEffectNumbers): string {
  const parts: string[] = [];
  if (e.damage > 0) parts.push(`${e.damage} ${e.damageType === "fire" ? "fire " : ""}damage`);
  if (e.burn) parts.push(`burning ${e.burn.perSecond} a second for ${sec(e.burn.seconds)}`);
  if (e.slow) parts.push(`slowed ${e.slow.percent}% for ${sec(e.slow.seconds)}`);
  if (e.stun) parts.push(`stunned ${sec(e.stun.seconds)} (bosses ${sec(e.stun.bossSeconds)})`);
  if (e.mark) parts.push(`marked ${sec(e.mark.seconds)} (+${e.mark.takenPercent}% damage taken${e.mark.pointsPercent > 0 ? `, +${e.mark.pointsPercent}% points` : ""})`);
  if (e.dazzleSeconds > 0) parts.push(`dazzled ${sec(e.dazzleSeconds)} (no harm to troops)`);
  return parts.join(", ");
}

/** Who a strike goes for, within its reach of the flag. */
function strikeWho(n: Extract<PetAbilityNumbers, { id: "strike" }>): string {
  const at = `within ${cells(n.reach)} of the flag`;
  if (n.targets === "airFirst") return `flyers first ${at}${n.airMultPercent > 100 ? ` (×${n.airMultPercent / 100} to a flyer)` : ""}`;
  return n.targets === "any" ? `the nearest raider ${at} (flyers too)` : `the nearest ground raider ${at}`;
}

/** Who a pulse or a signature touches. */
function areaWho(reach: number, air: boolean, strongest: boolean): string {
  return strongest ? `the toughest raider within ${cells(reach)}` : `raiders within ${cells(reach)}${air ? " (flyers too)" : ""}`;
}

/** An aura's parts, each a sentence's worth. */
function auraWords(n: Extract<PetAbilityNumbers, { id: "aura" }>): string {
  const at = `within ${cells(n.reach)} of the flag`, parts: string[] = [];
  if (n.towerAirPercent > 0) parts.push(`Towers deal +${n.towerAirPercent}% damage to flyers`);
  if (n.burningTakenPercent > 0) parts.push(`Burning raiders take +${n.burningTakenPercent}% damage`);
  if (n.troopGuardPercent > 0) parts.push(`Troops ${at} take −${n.troopGuardPercent}% damage`);
  if (n.heal) parts.push(`Every ${sec(n.heal.everySeconds)} the most hurt troop ${at} heals ${n.heal.percent}%`);
  if (n.killPointsPercent > 0) parts.push(`Raiders falling ${at} score +${n.killPointsPercent}% points`);
  return parts.join(" · ");
}

/** A pulse's parts: what it does to troops, then to raiders. */
function pulseWords(n: Extract<PetAbilityNumbers, { id: "pulse" }>): string {
  const parts: string[] = [];
  if (n.troopShieldPercent > 0) parts.push(`troops within ${cells(n.reach)} get a shield of ${n.troopShieldPercent}% of their health for ${sec(n.shieldSeconds)}`);
  if (n.troopHealPercent > 0) parts.push(`troops within ${cells(n.reach)} heal ${n.troopHealPercent}%`);
  if (n.effect) parts.push(`${areaWho(n.reach, n.air, n.strongest)}: ${effectWords(n.effect)}`);
  return `Every ${sec(n.everySeconds)}: ${parts.join("; ")}`;
}

/** What an ability does at these numbers, one line ("56 damage every 0.8 s · raiders 1.5 cells from the flag"). */
export function abilityWords(n: PetAbilityNumbers): string {
  switch (n.id) {
    case "bite": return `${n.damage} damage every ${sec(n.everySeconds)} · raiders ${cells(n.reach)} from the flag`;
    case "muster": return `Troops muster ${n.spawnCutPercent}% faster`;
    case "rallyBark": return `Every ${sec(n.everySeconds)}: troops by the flag +${n.damagePercent}% damage, −${n.guardPercent}% damage taken for ${sec(n.forSeconds)}`;
    case "howl": return `Once a wave: raiders within ${cells(n.reach)} stunned ${sec(n.stunSeconds)} (bosses ${sec(n.bossStunSeconds)})`;
    case "strike": {
      const extra = effectWords({ ...n.effect, damage: 0 });
      return `${n.damage} ${n.effect.damageType === "fire" ? "fire " : ""}damage every ${sec(n.everySeconds)} · ${strikeWho(n)}${extra ? ` · ${extra}` : ""}`;
    }
    case "aura": return auraWords(n);
    case "pulse": return pulseWords(n);
    case "signature": return `Once a wave: ${areaWho(n.reach, n.air, n.strongest)}: ${effectWords(n.effect)}`;
  }
}

/** The damage an ability's numbers deal (the number that grows ×1.07 a level), or 0. */
function headline(n: PetAbilityNumbers): number {
  switch (n.id) {
    case "bite": case "strike": return n.damage;
    case "pulse": return n.effect?.damage ?? 0;
    case "signature": return n.effect.damage;
    default: return 0;
  }
}

/** The headline number that grows with the level, for the sheet's "now → next" ("56 → 60 damage"); null when it does not grow. */
export function growth(line: PetAbilityLine): string | null {
  if (!line.now || !line.next) return null;
  const a = headline(line.now), b = headline(line.next);
  return b > a ? `${a} → ${b} damage` : null;
}
